import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useWalletConfig } from '@/hooks/useWalletConfig';

export const useWallet = () => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const walletConfig = useWalletConfig();
  const {
    connect,
    connectors,
    error: connectError,
    isPending,
    status,
  } = useConnect();
  const { disconnect } = useDisconnect();

  const hasWalletConnectProjectId = walletConfig.configured && !!walletConfig.walletConnectProjectId;
  const availableConnectors = connectors.filter((c) => {
    const id = (c.id || '').toLowerCase();
    const name = (c.name || '').toLowerCase();
    const looksLikeWalletConnect = id.includes('walletconnect') || name.includes('walletconnect');
    const looksLikeWeb3Modal = id.includes('web3modal') || name.includes('web3modal');

    const looksLikeWeb3ModalAuth = looksLikeWeb3Modal && (id.includes('auth') || name.includes('auth'));

    if (looksLikeWeb3ModalAuth) {
      return false;
    }

    if (!hasWalletConnectProjectId && (looksLikeWalletConnect || looksLikeWeb3Modal)) {
      return false;
    }

    return true;
  });

  const connectWallet = useCallback(() => {
    // Try WalletConnect first, fallback to injected
    const walletConnectConnector = availableConnectors.find((c) => {
      const id = (c.id || '').toLowerCase();
      const name = (c.name || '').toLowerCase();
      return id.includes('walletconnect') || name.includes('walletconnect');
    });
    const injectedConnector = availableConnectors.find((c) => {
      const id = (c.id || '').toLowerCase();
      const name = (c.name || '').toLowerCase();
      return id === 'injected' || name.includes('injected') || name.includes('browser');
    });
    
    const connector = walletConnectConnector || injectedConnector;
    if (connector) {
      connect({ connector });
    }
  }, [connect, availableConnectors]);

  const connectWithConnector = useCallback((connectorId: string) => {
    const connector = availableConnectors.find(c => c.id === connectorId);
    if (connector) {
      connect({ connector });
    }
  }, [connect, availableConnectors]);

  const handleDisconnect = useCallback(() => {
    disconnect();
    // Clear all cached data
    queryClient.clear();
    // Clear any localStorage data related to the wallet
    if (typeof window !== 'undefined') {
      // Clear any wallet-related localStorage items
      Object.keys(localStorage).forEach(key => {
        if (key.includes('wallet') || key.includes('wagmi') || key.includes('connected')) {
          localStorage.removeItem(key);
        }
      });
    }
    // Navigate to home page
    navigate('/');
    toast({
      title: 'Wallet Disconnected',
      description: 'Your wallet has been successfully disconnected.',
    });
  }, [disconnect, navigate, toast, queryClient]);

  return {
    address,
    isConnected,
    isConnecting: isPending,
    connectWallet,
    connectWithConnector,
    disconnect: handleDisconnect,
    connectors: availableConnectors,
    error: connectError,
    status,
  };
};
